// Primeiro, e de proposito: decide como todo scope da aplicacao reporta mudanca, e
// tem de rodar antes que exista um.
import './instrumentation'

import { onCleanup } from 'solid-js'
import { render } from 'solid-js/web'
import { bindScope } from 'wdc-cube-solid'

import {
    ApiClient,
    BackendTime,
    FileSaver,
    currentHistoryCodec,
    initializeRoutes,
    publicHistoryCodec,
    registerServices,
    sealAddress,
    SealedHistoryManager
} from 'mormaii-web-presentation'
import { MainPresenter } from 'mormaii-web-presentation/main'
import { SharedHistoryKeys } from 'mormaii-web-presentation/shared-history'

import { MainView } from './modules/main'
import { registerAllViews } from './modules/view-catalog'
import { saveInBrowser } from './lib/save-file'
import { applyCachedTheme } from './lib/apply-theme'

import './styles/index.css'

/**
 * O arranque do painel.
 *
 * Nasceu como o segundo renderizador - havia um em React ao lado, e este era o
 * experimento de GUI. O de React saiu em 16/09/2026; o que ficou da convivencia e o
 * desenho da fronteira: a apresentacao inteira vive em `mormaii-web-presentation`, e
 * aqui so se desenha. E por isso que este arquivo cuida da cifra do endereco e da
 * ponte dos links antigos - sao contrato com o backend e com os enderecos que ja
 * estao na rua, e nao assunto de view.
 */

ApiClient.INSTANCE.baseUrl = import.meta.env.VITE_API_URL

// O banco esta em UTC desde 27/09/2026: os instantes do backend sao lidos no fuso de quem olha
// (ver `domain/instant.ts`). Desligado so para um backend local sobre um dump anterior a virada.
BackendTime.utc = import.meta.env.VITE_DATAS_EM_UTC === 'true'

// Quem salva os arquivos que a apresentacao exporta (CSV, PDF): ela monta, o navegador salva.
FileSaver.INSTANCE.install(saveInBrowser)

// O tema do ultimo uso, antes da primeira pintura - ver `lib/apply-theme.ts`.
applyCachedTheme()

registerServices()
registerAllViews()
initializeRoutes()

/**
 * Ponte para os links de compartilhamento que ja estao na rua.
 *
 * Ver o gemeo em `renderer-react/src/main.tsx`: o backend monta
 * `{site}/shared-history/{uuid}`, e o identificador e um SEGMENTO DE CAMINHO, que no
 * Cube seria lido como um place inexistente. A traducao acontece aqui, na borda, e
 * sai SELADA - a aplicacao so aceita endereco que ela mesma produziu.
 */
function bridgeLegacySharedHistoryLink() {
    // O caminho e lido A PARTIR do base: servido em `/mormaii/`, o link chega como
    // `/mormaii/shared-history/{uuid}`. Ver DEV_BASE no `vite.config.ts`.
    const base = import.meta.env.BASE_URL
    const pathname = window.location.pathname
    const relativo = pathname.startsWith(base) ? `/${pathname.slice(base.length)}` : pathname
    const match = /^\/shared-(full-)?history\/([^/?#]+)/.exec(relativo)
    if (!match) {
        return
    }

    const [, full, code] = match
    const query = SharedHistoryKeys.legacyQuery(code, !!full)

    const codec = publicHistoryCodec()
    const target = codec ? `/shared-history?${codec.envelope}=${codec.encode(query)}` : `/shared-history?${query}`

    window.history.replaceState(null, '', `${window.location.origin}${base}#${target}`)
}

bridgeLegacySharedHistoryLink()

function App(props: { presenter: MainPresenter }) {
    onCleanup(props.presenter.initialize())

    // Todo outro scope e ligado pelo slot que o desenha; a raiz nao tem slot acima
    // dela, entao e ligada aqui.
    return <MainView scope={bindScope(props.presenter.scope)} />
}

/**
 * A cifra do endereco tem de estar instalada ANTES da aplicacao subir - o
 * `kickStart` le `historyManager.location` como primeira acao. Mesma razao e mesma
 * ordem do renderizador React.
 */
async function boot() {
    const root = document.getElementById('root')
    if (!root) {
        return
    }

    const historyManager = new SealedHistoryManager(true)

    try {
        historyManager.codec = await currentHistoryCodec()
    } catch (error) {
        console.warn('Sem cifra no endereço:', error)
    }

    if (import.meta.env.DEV) {
        Object.assign(window, {
            __sealAddress: (plain: string) => sealAddress(plain, historyManager.codec)
        })
    }

    const presenter = new MainPresenter(historyManager)
    render(() => <App presenter={presenter} />, root)
}

void boot()
